import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ADMIN_IDS } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import visionaLogo from "../../img/visiona-logo.png";
import wecastLogo from "../../img/wecast-logo.png";
import { getConvertedImporte, getRateForOpp } from "../../utils";
import { calcularComisionesFijasMensuales } from "../../utils/comisionEdificiosUtil";
import { crearConvertirAMonedaLocal } from "../../utils/convertirAMonedaLocal";
import { useExchangeRates } from "../../utils/useExchangeRates";
import { meses, mesesCompletos, windowArray, interesesMensualesUSD, impuestosMensualesUSD } from "../ER/constants";
// Hooks de cálculo puros del v1: se importan SOLO EN LECTURA (el v1 queda congelado).
// Los que necesitaron cambio viven copiados en ./hooks.
import { useBTLCalcs } from "../ER/hooks/useBTLCalcs";
import { useContratosCalcs } from "../ER/hooks/useContratosCalcs";
import { useEBITDACalcs } from "../ER/hooks/useEBITDACalcs";
import { useERFormatting } from "../ER/hooks/useERFormatting";
import { useGestionCalcs } from "../ER/hooks/useGestionCalcs";
import { useHardwareCalcs } from "../ER/hooks/useHardwareCalcs";
import { usePublicidadCalcs } from "../ER/hooks/usePublicidadCalcs";
import { BloqueBTable } from "./components/BloqueBTable";
import { BloqueCTable } from "./components/BloqueCTable";
import { BloqueDTable } from "./components/BloqueDTable";
import { CostosFijosTable } from "./components/CostosFijosTable";
import { DetalleFacturasModal } from "./components/DetalleFacturasModal";
import { EBITDAResultadoTable } from "./components/EBITDAResultadoTable";
import { ERHeader } from "./components/ERHeader";
import { InversionActivosTable } from "./components/InversionActivosTable";
import { TipoCambioTable } from "./components/TipoCambioTable";
// Costos fijos del v2: los 12 del v1 + Servicios Online. Se importan de acá y NO de
// ../ER/constants — el v1 está congelado y no puede ganar una fila (ver ./constants.js).
import { camposCostosFijosV2 as camposCostosFijos, nombresCostosFijosV2 as nombresCostosFijos } from "./constants";
import { agregarCostosER } from "./costosER";
import { useERv2Data } from "./hooks/useERv2Data";
import { useRRHHCalculations } from "./hooks/useRRHHCalculations";
import { contarPantallasPublicidad, contarPantallasCarteleria, repartirPorPantallas } from "./pantallasOperativas";
import styles from "./stylesERv2.module.scss";

const ZEROS = Array(12).fill(0);
const VACIO = [];

// El país sale de la sesión, igual que el v1. El año arranca en 2026 (primer año con
// contabilidad en la plataforma) pero el selector es libre.
const ANO_INICIAL = 2026;

// Los 3 conceptos del Bloque A, con los mismos labels que el v1.
const CONCEPTOS_MANUALES_A = ["Servicios internet", "Software DS", "Mantenimiento"];

// Desde este año el costo sale de las facturas; antes, de las tablas manuales del v1.
const ANO_INICIO_FACTURAS = 2026;

const sumar = (...arrays) => ZEROS.map((_, i) => arrays.reduce((acc, a) => acc + ((a && a[i]) || 0), 0));
const restar = (base, ...arrays) =>
  ZEROS.map((_, i) => (base[i] || 0) - arrays.reduce((a, b) => a + ((b && b[i]) || 0), 0));

export const ERv2 = () => {
  const { id: userId, pais } = useAuthContext();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const [selectedYear, setSelectedYear] = useState(ANO_INICIAL);
  const [verEnDolares, setVerEnDolares] = useState(false);
  const [verChileYPeru, setVerChileYPeru] = useState(false);
  const [verUltimos12Meses, setVerUltimos12Meses] = useState(false);
  const [verAcumulados, setVerAcumulados] = useState(false);
  const [verUnitEconomics, setVerUnitEconomics] = useState(false);
  const [verInversionActivos, setVerInversionActivos] = useState(false);
  const [bloqueACollapsed, setBloqueACollapsed] = useState(true);
  const [bloqueBCollapsed, setBloqueBCollapsed] = useState(true);
  const [bloqueCCollapsed, setBloqueCCollapsed] = useState(true);
  const [bloqueDCollapsed, setBloqueDCollapsed] = useState(true);
  const [costosFijosCollapsed, setCostosFijosCollapsed] = useState(true);
  const [rrhhCollapsed, setRrhhCollapsed] = useState(true);
  // Mismo default que el v1: EBIT y Resultado arrancan colapsados.
  const [ebitCollapsed, setEbitCollapsed] = useState(true);
  const [resultadoCollapsed, setResultadoCollapsed] = useState(true);
  const [inversionCollapsed, setInversionCollapsed] = useState(true); // mismo default que el v1
  const [detalleFacturas, setDetalleFacturas] = useState(null);

  const monthsToShow = selectedYear < currentYear ? 12 : currentMonth;
  // Con LTM se muestran los 12 meses de la ventana, no solo los transcurridos.
  const mesesVisibles = verUltimos12Meses ? 12 : monthsToShow;

  const {
    oportunidades,
    clientes,
    intermediarios,
    edificios,
    snapMap,
    pctMap,
    costosMap,
    users,
    negociosCarteleria,
    salesBTL,
    salesBTLAnoAnterior,
    salesBTLOtroPais,
    oportunidadesChile,
    oportunidadesPeru,
    contratosChile,
    contratosPeru,
    clientesChile,
    clientesPeru,
    intermediariosChile,
    intermediariosPeru,
    edificiosChile,
    edificiosPeru,
    snapMapChile,
    snapMapPeru,
    pctMapChile,
    pctMapPeru,
    costosMapChile,
    costosMapPeru,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    salesBTLChile,
    salesBTLPeru,
    costosGestionData,
    depreciacionMensual,
    amortizacionMensual,
    depreciacionMensualChile,
    depreciacionMensualPeru,
    amortizacionMensualChile,
    amortizacionMensualPeru,
    facturasSII,
    facturasExternas,
    boletasHonorarios,
    facturasSIIChile,
    facturasSIIPeru,
    facturasExternasChile,
    facturasExternasPeru,
    boletasHonorariosChile,
    boletasHonorariosPeru,
    inversionMensual,
    inversionMensualChile,
    inversionMensualPeru,
    valorActivosMensual,
    valorActivosMensualChile,
    valorActivosMensualPeru,
    empleadosRRHHChile,
    empleadosRRHHPeru,
    manual,
    dtesSinVincular,
    siiVinculacionesMap,
    datosCombinadosCargados,
    isLoading,
  } = useERv2Data(pais, selectedYear);

  // Mismo flag que el v1 (ER/index.js:407).
  const modoCombinado = verChileYPeru && datosCombinadosCargados;

  const {
    dolarFallback,
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarMensualChile,
    dolarMensualPeru,
    dolarMensualOtroPais,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    getUFValue,
  } = useExchangeRates(pais, selectedYear);

  const { moneda, formatCurrency, localString } = useERFormatting(
    pais,
    verEnDolares,
    false,
    dolarMensual,
    monthsToShow,
    dolarFallback
  );

  // Misma fuente que el v1 (src/utils/convertirAMonedaLocal.js). La necesitan los
  // bloques B y C para reconocer el INGRESO del mes.
  const convertirAMonedaLocal = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValue]
  );

  // Variantes POR PAÍS para el modo región. `getDolarValue` está atado al país de la sesión
  // (useExchangeRates.js:76), así que convertir las externas en US$ de Chile desde una sesión
  // Perú las pasaría por el TC del sol y las aplastaría a ~1/253 de su valor. El hook ya
  // expone `getDolarValueChile` / `getDolarValuePeru`: cada pata usa el suyo.
  const convertirAMonedaLocalChile = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue: getDolarValueChile,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValueChile]
  );
  const convertirAMonedaLocalPeru = useMemo(
    () =>
      crearConvertirAMonedaLocal({
        costosGestionData,
        getUFValue,
        getDolarValue: getDolarValuePeru,
        anoPorDefecto: selectedYear,
      }),
    [costosGestionData, selectedYear, getUFValue, getDolarValuePeru]
  );

  // Bloque C: antes de 2026 se lee la tabla manual como el v1; desde 2026 va en cero
  // (el software de carteleria sale del estimado y "Otros" no tiene cuenta que lo
  // alimente). Una sola fuente por mes.
  const getCostoGestionValue = useCallback(
    // Misma firma y mismos filtros que el v1 (ER/index.js:328), incluido negocio_id:
    // sin ese filtro se toma la primera fila del mes y el valor sale distinto.
    (monthIndex, field, negocioId = null) => {
      if (selectedYear >= ANO_INICIO_FACTURAS) return 0;
      const row = (costosGestionData || []).find(
        (c) => Number(c.mes) === monthIndex + 1 && Number(c.ano) === selectedYear && c.negocio_id === negocioId
      );
      return Number(row?.[field]) || 0;
    },
    [costosGestionData, selectedYear]
  );

  // ─── A — Publicidad (pauta) ─────────────────────────────────
  const {
    valoresMensuales: pubIngresos,
    valoresMensualesAnoAnterior: pubIngresosAA,
    publicidadMensualesCombinados: pubCombinados,
    valoresMensualesComisionAgenciaAnoAnterior: pubAgenciaAA,
    valoresMensualesComisionBrokerAnoAnterior: pubBrokerAA,
    valoresMensualesTakeAnoAnterior: pubTakeAA,
    valoresMensualesApxAnoAnterior: pubApxAA,
    valoresMensualesComisionesAnoAnterior: pubEdificiosAA,
    valoresMensualesComisionComercialAnoAnterior: pubComercialAA,
    valoresMensualesComisionAgencia: pubAgencia,
    valoresMensualesComisionBroker: pubBroker,
    valoresMensualesTake: pubTake,
    valoresMensualesApx: pubApx,
    valoresMensualesComisiones: pubEdificios,
    valoresMensualesComisionComercial: pubComercial,
    valoresMensualesComisionAgenciaChile: pubAgenciaChile,
    valoresMensualesComisionAgenciaPeru: pubAgenciaPeru,
    valoresMensualesComisionBrokerChile: pubBrokerChile,
    valoresMensualesComisionBrokerPeru: pubBrokerPeru,
    valoresMensualesTakeChile: pubTakeChile,
    valoresMensualesTakePeru: pubTakePeru,
    valoresMensualesApxChile: pubApxChile,
    valoresMensualesApxPeru: pubApxPeru,
    valoresMensualesComisionesChile: pubEdificiosChile,
    valoresMensualesComisionesPeru: pubEdificiosPeru,
    valoresMensualesComisionComercialChile: pubComercialChile,
    valoresMensualesComisionComercialPeru: pubComercialPeru,
  } = usePublicidadCalcs({
    pais,
    selectedYear,
    monthsToShow,
    currentYear,
    modoCombinado,
    oportunidades,
    oportunidadesChile,
    oportunidadesPeru,
    clientes,
    clientesChile,
    clientesPeru,
    intermediarios,
    intermediariosChile,
    intermediariosPeru,
    edificios,
    edificiosChile,
    edificiosPeru,
    snapMap,
    snapMapChile,
    snapMapPeru,
    pctMap,
    pctMapChile,
    pctMapPeru,
    costosMap,
    costosMapChile,
    costosMapPeru,
    users,
    isOportunidadesLoading: isLoading,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
  });

  // ─── Contratos multi-mes (se suman a publicidad) ────────────
  const {
    ingresosContratosMensual,
    ingresosContratosMensualAnoAnterior,
    contratosMensualesCombinados,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionAgenciaContratosMensualAnoAnterior,
    comisionBrokerContratosMensualAnoAnterior,
    comisionApxContratosMensualAnoAnterior,
    comisionComercialContratosMensualAnoAnterior,
    comisionEdificiosContratosMensual,
    comisionAgenciaContratosMensual,
    comisionBrokerContratosMensual,
    comisionApxContratosMensual,
    comisionComercialContratosMensual,
    comisionEdificiosContratosMensualChile,
    comisionEdificiosContratosMensualPeru,
    comisionAgenciaContratosMensualChile,
    comisionAgenciaContratosMensualPeru,
    comisionBrokerContratosMensualChile,
    comisionBrokerContratosMensualPeru,
    comisionApxContratosMensualChile,
    comisionApxContratosMensualPeru,
    comisionComercialContratosMensualChile,
    comisionComercialContratosMensualPeru,
  } = useContratosCalcs({
    contratosChile,
    contratosPeru,
    selectedYear,
    modoCombinado,
    dolarFallback,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
    dolarMensualChile,
    dolarMensualPeru,
    pais,
    edificios,
    edificiosChile,
    edificiosPeru,
    snapMap,
    snapMapChile,
    snapMapPeru,
    pctMap,
    pctMapChile,
    pctMapPeru,
    clientes,
    clientesChile,
    clientesPeru,
    intermediarios,
    intermediariosChile,
    intermediariosPeru,
  });

  // ─── B — Hardware (solo ingresos; los 4 costos son manuales → 0) ───
  const {
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosHardwareMensualChile,
    ingresosHardwareMensualPeru,
    compraHardwareMensual,
    instalacionMensual,
    materialesMensual,
    otrosCostosMensual,
    compraHardwareMensualChile,
    instalacionMensualChile,
    materialesMensualChile,
    otrosCostosMensualChile,
    compraHardwareMensualPeru,
    instalacionMensualPeru,
    materialesMensualPeru,
    otrosCostosMensualPeru,
  } = useHardwareCalcs({
    pais,
    selectedYear,
    monthsToShow,
    negociosCarteleria,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    costosHardwareFlex: manual.hardwareFlex || VACIO,
    costosHardwareFlexAnoAnterior: manual.hardwareFlexAA || VACIO,
    costosHardwareFlexChile: manual.porPais?.Chile?.hardwareFlex || VACIO,
    costosHardwareFlexPeru: manual.porPais?.["Perú"]?.hardwareFlex || VACIO,
    costosGestionData,
    getUFValue,
    getDolarValue,
    getDolarValueChile,
    getDolarValuePeru,
  });

  // ─── C — Gestión & Contenidos (solo ingresos) ───────────────
  const {
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    gestionMensualesCombinados,
    detalleGestionContenidosPorMes,
    ytdIngresosGestionContenidos,
    calcularCostoSoftwareEstimado,
    ytdSoftwareDS,
    ytdOtros,
    margenGestionContenidosMensual,
    ytdMargenGestionContenidos,
  } = useGestionCalcs({
    pais,
    selectedYear,
    monthsToShow,
    currentYear,
    modoCombinado,
    negociosCarteleria,
    negociosCarteleriaChile,
    negociosCarteleriaPeru,
    clientes,
    edificios,
    costosGestionData,
    costosPublicidadData: Array(12).fill(null),
    convertirAMonedaLocal,
    getCostoGestionValue,
    getDolarValue,
    dolarMensualPeru,
    dolarMensualChile,
    dolarFallback,
    modalRefresh: 0,
  });

  // ─── D — BTL (ingresos + comisión edificios; otros costos manuales → 0) ───
  const {
    ingresosBTLMensual,
    ingresosBTLMensualAnoAnterior,
    ingresosBTLMensualChile,
    ingresosBTLMensualPeru,
    comisionEdificiosBTLMensual,
    comisionEdificiosBTLMensualAnoAnterior,
    otrosCostosBTLMensual,
    otrosCostosBTLMensualChile,
    otrosCostosBTLMensualPeru,
    comisionEdificiosBTLMensualChile,
    comisionEdificiosBTLMensualPeru,
  } = useBTLCalcs({
    salesBTL,
    salesBTLAnoAnterior,
    salesBTLChile,
    salesBTLPeru,
    salesBTLOtroPais,
    selectedYear,
    monthsToShow,
    modoCombinado,
  });

  // ─── RRHH ───────────────────────────────────────────────────
  const {
    empleadosRRHH,
    isEmpleadosRRHHLoading,
    getSueldoBaseVigente,
    rrhhMensualConBeneficios,
    rrhhMensualConBeneficiosAnoAnterior,
    rrhhMensualChile,
    rrhhMensualPeru,
  } = useRRHHCalculations(pais, selectedYear, monthsToShow, empleadosRRHHChile, empleadosRRHHPeru);

  // ─── Combinaciones (mismo orden que ER/index.js:1585-1728) ──
  const comisionesFijasMensual = useMemo(
    () => calcularComisionesFijasMensuales(edificios, selectedYear, getDolarValue),
    [edificios, selectedYear, getDolarValue]
  );
  // Variantes por pais para el agregado de region (v1: ER/index.js:1575 y :1579).
  const comisionesFijasMensualChile = useMemo(
    () => calcularComisionesFijasMensuales(edificiosChile, selectedYear, getDolarValueChile),
    [edificiosChile, selectedYear, getDolarValueChile]
  );
  const comisionesFijasMensualPeru = useMemo(
    () => calcularComisionesFijasMensuales(edificiosPeru, selectedYear, getDolarValuePeru),
    [edificiosPeru, selectedYear, getDolarValuePeru]
  );

  const facturacionA = useMemo(
    () => sumar(pubIngresos, ingresosContratosMensual),
    [pubIngresos, ingresosContratosMensual]
  );

  const costoIntermediacion = useMemo(
    () =>
      sumar(
        pubAgencia,
        comisionAgenciaContratosMensual,
        pubBroker,
        comisionBrokerContratosMensual,
        pubTake,
        pubApx,
        comisionApxContratosMensual
      ),
    [
      pubAgencia,
      comisionAgenciaContratosMensual,
      pubBroker,
      comisionBrokerContratosMensual,
      pubTake,
      pubApx,
      comisionApxContratosMensual,
    ]
  );

  const comisionEdificios = useMemo(
    () => sumar(pubEdificios, comisionEdificiosContratosMensual, comisionesFijasMensual),
    [pubEdificios, comisionEdificiosContratosMensual, comisionesFijasMensual]
  );

  const comisionComercial = useMemo(
    () => sumar(pubComercial?.valores, comisionComercialContratosMensual),
    [pubComercial, comisionComercialContratosMensual]
  );

  const facturacion = useMemo(
    () => sumar(facturacionA, ingresosHardwareMensual, ingresosGestionContenidosMensual, ingresosBTLMensual),
    [facturacionA, ingresosHardwareMensual, ingresosGestionContenidosMensual, ingresosBTLMensual]
  );

  // ─── MOTOR ÚNICO DE COSTOS (fase 2) ─────────────────────────
  // Todas las líneas de costo que el v1 carga a mano salen de acá. Un solo agregado
  // del que leen fila, Contribución, Costos Variables, Costos Fijos, EBITDA y YTD.
  const costos = useMemo(
    () =>
      agregarCostosER({
        facturasSII,
        facturasExternas,
        boletasHonorarios,
        ano: selectedYear,
        pais,
        convertirAMonedaLocal,
      }),
    [facturasSII, facturasExternas, boletasHonorarios, selectedYear, pais, convertirAMonedaLocal]
  );

  // ─── FUENTE DEL COSTO POR AÑO ───────────────────────────────
  // El unico delta del v2 contra el v1: desde 2026-01-01 los costos salen de las
  // facturas; 2025 y para atras siguen saliendo de las tablas manuales, con la misma
  // logica del v1 (proyeccion incluida). Una sola fuente por mes, sin doble conteo.
  const usaFacturas = (ano) => ano >= ANO_INICIO_FACTURAS;

  // Proyeccion de los conceptos manuales del bloque A (ER/index.js:1261).
  const proyectarPublicidad = useCallback((data, dataAA, campo, monthIndex) => {
    const valores = [];
    for (let i = 0; i < monthIndex; i++) {
      const v = data?.[i]?.[campo];
      if (v != null && Number(v) > 0) valores.push(Number(v));
    }
    if (dataAA?.length > 0) {
      for (let i = 11; i >= 0 && valores.length < 12; i--) {
        const v = dataAA[i]?.[campo];
        if (v != null && Number(v) > 0) valores.push(Number(v));
      }
    }
    if (valores.length === 0) return 0;
    return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
  }, []);

  const serieManualPublicidad = useCallback(
    (data, dataAA, campo) =>
      ZEROS.map((_, i) => {
        const row = data?.[i];
        if (row && row[campo] != null) return Number(row[campo]) || 0;
        return proyectarPublicidad(data, dataAA, campo, i);
      }),
    [proyectarPublicidad]
  );

  // Proyeccion de los costos fijos (ER/hooks/useCostosFijosCalcs.js:39 y :128).
  const serieManualFija = useCallback(
    (raw, data, dataAA, campo) =>
      ZEROS.map((_, i) => {
        const existe = (raw || []).some((r) => Number(r.mes) === i + 1 && r[campo] != null);
        if (existe) return Number(data?.[i]?.[campo]) || 0;
        const valores = [];
        for (let j = 0; j < i; j++) {
          const v = data?.[j]?.[campo];
          if (v > 0) valores.push(v);
        }
        if (dataAA?.length > 0) {
          for (let j = 11; j >= 0 && valores.length < 12; j--) {
            const v = dataAA[j]?.[campo];
            if (v > 0) valores.push(v);
          }
        }
        if (valores.length === 0) return 0;
        return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
      }),
    []
  );

  // Agregados POR PAÍS para el modo región. La vista de país único sigue usando `costos`
  // (la sesión); estos dos existen para que la pata de Chile del combinado tome las facturas
  // de Chile aunque se entre desde visiona.pe, donde `pais` es "Perú" (auth.js deriva el país
  // del hostname). Perú no tiene SII/SUNAT: hoy su agregado da 0, pero se arma igual en vez
  // de hardcodear ZEROS, para que el día que tenga facturas entre solo.
  const costosChile = useMemo(
    () =>
      agregarCostosER({
        facturasSII: facturasSIIChile,
        facturasExternas: facturasExternasChile,
        boletasHonorarios: boletasHonorariosChile,
        ano: selectedYear,
        pais: "Chile",
        convertirAMonedaLocal: convertirAMonedaLocalChile,
      }),
    [facturasSIIChile, facturasExternasChile, boletasHonorariosChile, selectedYear, convertirAMonedaLocalChile]
  );
  const costosPeru = useMemo(
    () =>
      agregarCostosER({
        facturasSII: facturasSIIPeru,
        facturasExternas: facturasExternasPeru,
        boletasHonorarios: boletasHonorariosPeru,
        ano: selectedYear,
        pais: "Perú",
        convertirAMonedaLocal: convertirAMonedaLocalPeru,
      }),
    [facturasSIIPeru, facturasExternasPeru, boletasHonorariosPeru, selectedYear, convertirAMonedaLocalPeru]
  );

  // Mismo motor, un anio atras: la ventana LTM necesita los costos del anio anterior.
  const costosAA = useMemo(
    () =>
      agregarCostosER({
        facturasSII,
        facturasExternas,
        boletasHonorarios,
        ano: selectedYear - 1,
        pais,
        convertirAMonedaLocal,
      }),
    [facturasSII, facturasExternas, boletasHonorarios, selectedYear, pais, convertirAMonedaLocal]
  );

  // Bloque A. Software DS del v1 = max(0, total − estimado cartelería); eliminado el
  // estimado de 10,99 USD/pantalla (spec fase 2), el término restado es 0 → A se queda
  // con las facturas de la cuenta y la fila de C, cuya única fuente era ese estimado,
  // queda en 0. Misma fórmula del v1, con el término anulado.
  // Por año: facturas desde 2026, tablas manuales antes (con la proyeccion del v1).
  const porAno = useCallback(
    (ano, desdeFacturas, desdeManual) => (usaFacturas(ano) ? desdeFacturas : desdeManual),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const manualServiciosInternet = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "servicios_internet");
  const manualMantenimiento = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "mantenimiento");
  const manualSoftwareDSTotal = serieManualPublicidad(manual.publicidad, manual.publicidadAA, "software_ds");

  const aServiciosInternet = porAno(selectedYear, costos.porCuenta("servicios_internet"), manualServiciosInternet);
  // Software DS: el v1 reparte el software entre publicidad y carteleria — C se lleva
  // el estimado de carteleria (pantallas x 10,99 USD) y A el resto. Se replica igual;
  // la UNICA diferencia es que el crudo de A sale de las facturas de la cuenta
  // software_ds en vez del monto manual (v1: useGestionCalcs getSoftwareDSValueA).
  const softwareDSFacturas = porAno(selectedYear, costos.porCuenta("software_ds"), manualSoftwareDSTotal);
  const aSoftwareDS = useMemo(
    () => ZEROS.map((_, i) => Math.max(0, (softwareDSFacturas[i] || 0) - calcularCostoSoftwareEstimado(i))),
    [softwareDSFacturas, calcularCostoSoftwareEstimado]
  );
  const aMantenimiento = porAno(selectedYear, costos.porCuenta("mantenimiento"), manualMantenimiento);

  // ─── CONTENIDOS DIGITALES — reparto proporcional A/C por pantallas ──────────
  // Screenfeed / AccuWeather / ApiFootball: el servicio corre sobre las pantallas de los DOS
  // países, pero sus facturas están todas cargadas en Chile. El costo se reparte entre
  // Publicidad (A) y Cartelería (C) en proporción al inventario de cada circuito, sumando
  // Chile + Perú en ambos denominadores.
  //
  // NO se toca el ER de Perú: de Perú entra solo su CONTEO de pantallas. Sus líneas de costo
  // quedan exactamente como estaban (su agregado de facturas no tiene esta cuenta).
  //
  // A diferencia de Software DS, el reparto es PROPORCIONAL PURO: A + C === costo del mes.
  // No se reusa `calcularCostoSoftwareEstimado` — ese es un estimado residual, otra cosa.
  const pantallasPublicidad = useMemo(
    () => contarPantallasPublicidad(edificiosChile) + contarPantallasPublicidad(edificiosPeru),
    [edificiosChile, edificiosPeru]
  );
  const pantallasCarteleria = useMemo(
    () => contarPantallasCarteleria(negociosCarteleriaChile) + contarPantallasCarteleria(negociosCarteleriaPeru),
    [negociosCarteleriaChile, negociosCarteleriaPeru]
  );

  // Pre-2026 en 0: no hay tabla manual de contenidos y el motor corta por fecha, así que la
  // paridad diff-cero contra el v1 en 2025 no se mueve.
  const contenidosDigitalesTotal = porAno(selectedYear, costos.porCuenta("contenidos_digitales"), ZEROS);
  const contenidosReparto = useMemo(
    () =>
      contenidosDigitalesTotal.map((monto) => repartirPorPantallas(monto, pantallasPublicidad, pantallasCarteleria)),
    [contenidosDigitalesTotal, pantallasPublicidad, pantallasCarteleria]
  );
  const aContenidosDigitales = useMemo(() => contenidosReparto.map((r) => r.publicidad), [contenidosReparto]);
  const cContenidosDigitales = useMemo(() => contenidosReparto.map((r) => r.carteleria), [contenidosReparto]);

  // Contribución del Bloque C. `useGestionCalcs` (hook del v1, importado en lectura) la
  // calcula sin conocer esta cuenta — no la lee nadie hasta ahora —, así que la parte C del
  // reparto se resta acá en vez de tocar el hook, que está congelado con el v1.
  const margenGestionContenidosAjustado = useMemo(
    () => restar(margenGestionContenidosMensual || ZEROS, cContenidosDigitales),
    [margenGestionContenidosMensual, cContenidosDigitales]
  );
  // El helper `ytd` se define más abajo en el componente, así que acá se suma inline con el
  // mismo criterio (los meses visibles) para no quedar en la zona muerta del const.
  const ytdMargenGestionContenidosAjustado = useMemo(
    () =>
      (Number(ytdMargenGestionContenidos) || 0) -
      cContenidosDigitales.slice(0, mesesVisibles).reduce((a, b) => a + (b || 0), 0),
    [ytdMargenGestionContenidos, cContenidosDigitales, mesesVisibles]
  );

  // Bloque B: se conservan las 4 filas del v1. Solo "Compra hardware" tiene cuenta que
  // la alimente (`hardware`); Instalación, Materiales y Otros costos no tienen cuenta.
  const bCompraHardware = porAno(selectedYear, costos.porCuenta("hardware"), compraHardwareMensual);
  const bInstalacion = porAno(selectedYear, ZEROS, instalacionMensual);
  const bMateriales = porAno(selectedYear, ZEROS, materialesMensual);
  const bOtrosHardware = porAno(selectedYear, ZEROS, otrosCostosMensual);

  // Total de costo del Bloque A. Incluye la parte de contenidos digitales que le tocó al
  // circuito de publicidad → baja la Contribución A y entra a Costos Variables.
  const costosManualesA = useMemo(
    () => sumar(aServiciosInternet, aSoftwareDS, aMantenimiento, aContenidosDigitales),
    [aServiciosInternet, aSoftwareDS, aMantenimiento, aContenidosDigitales]
  );

  const contribucionA = useMemo(
    () => restar(facturacionA, costoIntermediacion, comisionEdificios, comisionComercial, costosManualesA),
    [facturacionA, costoIntermediacion, comisionEdificios, comisionComercial, costosManualesA]
  );

  // Misma composición que useEBITDACalcs.js:116-142 del v1.
  // Software de carteleria: en el v1 entra al agregado como `softwareUsado`
  // (useEBITDACalcs.js:122). Aca es el estimado, porque el v2 no tiene monto manual en C.
  // sales_btl.otros_costos es carga manual: solo alimenta los meses pre-2026.
  const otrosBTL = porAno(selectedYear, ZEROS, otrosCostosBTLMensual);

  // Software de carteleria: igual que el v1 (useEBITDACalcs.js:122), valor manual si
  // existe y estimado si no. Pre-2026 el manual viene de costos_gestion_contenidos;
  // desde 2026 getCostoGestionValue devuelve 0 y queda el estimado.
  const softwareCarteleria = useMemo(
    () =>
      ZEROS.map((_, i) => {
        if (i >= monthsToShow) return 0;
        const manualC = Number(getCostoGestionValue(i, "software_ds")) || 0;
        return manualC > 0 ? manualC : calcularCostoSoftwareEstimado(i);
      }),
    [calcularCostoSoftwareEstimado, getCostoGestionValue, monthsToShow]
  );

  // "Otros" del bloque C: carga manual, solo pre-2026 (getCostoGestionValue ya corta).
  const otrosGestion = useMemo(
    () => ZEROS.map((_, i) => (i < monthsToShow ? Number(getCostoGestionValue(i, "otros")) || 0 : 0)),
    [getCostoGestionValue, monthsToShow]
  );

  const costosVariables = useMemo(
    () =>
      sumar(
        costoIntermediacion,
        comisionEdificios,
        comisionComercial,
        comisionEdificiosBTLMensual,
        costosManualesA,
        bCompraHardware,
        bInstalacion,
        bMateriales,
        bOtrosHardware,
        softwareCarteleria,
        // La pata C del reparto. La pata A ya viaja dentro de costosManualesA, así que entre
        // las dos suman el costo completo del mes — ni de más ni de menos.
        cContenidosDigitales,
        otrosGestion,
        otrosBTL
      ),
    [
      costoIntermediacion,
      comisionEdificios,
      comisionComercial,
      comisionEdificiosBTLMensual,
      costosManualesA,
      bCompraHardware,
      cContenidosDigitales,
    ]
  );

  // Costos Fijos = RRHH (nómina) + los 12 conceptos, cada uno alimentado por su cuenta
  // homónima. `camposCostosFijos` coincide 1:1 con los `value` de CUENTAS_ER.
  const costosFijosPorConcepto = useMemo(
    () =>
      camposCostosFijos.map((campo) =>
        porAno(
          selectedYear,
          costos.porCuenta(campo),
          serieManualFija(manual.rawFijos, manual.fijos, manual.fijosAA, campo)
        )
      ),
    [costos, selectedYear, porAno, serieManualFija, manual]
  );

  const costosFijos = useMemo(
    () => sumar(rrhhMensualConBeneficios, ...costosFijosPorConcepto),
    [rrhhMensualConBeneficios, costosFijosPorConcepto]
  );

  // Subfila Gratificaciones: se deja IDENTICA al v1, con su bug incluido (IMM=500.000
  // hardcodeado sobre una base que ya trae la gratificacion). El TOTAL de RRHH si es
  // correcto y coincide al peso con el v1 — solo difiere ese display. La correccion
  // real se define aparte y entra por el v1, no por una divergencia del v2.

  // EBITDA, EBIT, Intereses, Impuestos y Beneficio Neto los calcula y renderiza
  // EBITDAResultadoTable (clonado del v1), alimentado desde el motor único de costos
  // vía totalCostosMes / getCostoFijoValor. No se duplica el cálculo acá.

  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";

  // Banner de ventas cerradas (100%) del mes anterior sin vincular.
  // Portado literal de ER/index.js:3294.
  const ventasCerradasSinVincular = useMemo(() => {
    if (!siiVinculacionesMap) return null;
    const contratos = pais === "Chile" ? contratosChile : contratosPeru;

    const tieneVinculacionSII = (refKey, facturanumero) => {
      if (siiVinculacionesMap._byRef[refKey]) return true;
      if (facturanumero && siiVinculacionesMap[String(facturanumero)]) return true;
      return false;
    };

    const hoy = new Date();
    const mesActualIdx = hoy.getMonth();
    const mesAnteriorIdx = mesActualIdx === 0 ? 11 : mesActualIdx - 1;
    const anoMesFactura = mesActualIdx === 0 ? selectedYear - 1 : selectedYear;

    const pendientesPauta = oportunidades.filter((opp) => {
      if (opp.eliminada) return false;
      if (opp.estatus !== "100%") return false;
      if (pais === "Chile") {
        if (tieneVinculacionSII(`sales:${opp.id}:`, opp.facturanumero)) return false;
      } else if (opp.facturanumero || opp.facturafecha) {
        return false;
      }
      if (opp.ano !== selectedYear) return false;
      if (!opp.mes_factura) return false;
      const partes = String(opp.mes_factura).split("-");
      if (partes.length !== 2) return false;
      const mesOpp = parseInt(partes[0], 10);
      const anoOpp = parseInt(partes[1], 10);
      if (isNaN(mesOpp) || isNaN(anoOpp)) return false;
      return mesOpp - 1 === mesAnteriorIdx && anoOpp === anoMesFactura;
    });

    const pendientesContratos = [];
    (contratos || []).forEach((c) => {
      if (c.eliminada) return;
      if (c.estatus !== "100%") return;
      (c.meses || []).forEach((mesObj, idx) => {
        if (!mesObj?.mes) return;
        const partes = String(mesObj.mes).split("-");
        if (partes.length !== 2) return;
        const mesOpp = parseInt(partes[0], 10);
        const anoOpp = parseInt(partes[1], 10);
        if (isNaN(mesOpp) || isNaN(anoOpp)) return;
        if (mesOpp - 1 !== mesAnteriorIdx || anoOpp !== anoMesFactura) return;
        if (pais === "Chile") {
          if (tieneVinculacionSII(`sales_contratos:${c.id}:${idx}`, mesObj.facturanumero)) return;
        } else if (mesObj.facturanumero || mesObj.facturafecha) {
          return;
        }
        pendientesContratos.push({
          id: `contrato-${c.id}-${idx}`,
          importe: Number(mesObj.importe) || 0,
          moneda_dolar: c.moneda_dolar || false,
          facturafecha: mesObj.facturafecha || null,
          fecha_cierre: c.fecha_cierre || null,
          mes_cierre: c.mes_cierre || null,
        });
      });
    });

    const filtradas = [...pendientesPauta, ...pendientesContratos];
    if (filtradas.length === 0) return null;

    const total = filtradas.reduce(
      (sum, opp) => sum + getConvertedImporte(opp, getRateForOpp(opp, getDolarValue, dolarFallback[pais])),
      0
    );
    return { count: filtradas.length, total, mesIdx: mesAnteriorIdx };
  }, [
    oportunidades,
    contratosChile,
    contratosPeru,
    selectedYear,
    pais,
    getDolarValue,
    dolarFallback,
    siiVinculacionesMap,
  ]);

  const ytd = (arr) => (arr || ZEROS).slice(0, mesesVisibles).reduce((a, b) => a + (b || 0), 0);

  // ─── Helpers con la misma forma que el v1 (ER/index.js:2304, :2311, :2200) ───
  const formatPercentage = (value) => {
    if (!value || value === 0) return "0%";
    const dec = Math.abs(value) < 10 ? 1 : 0;
    return value.toLocaleString("es-CL", { minimumFractionDigits: dec, maximumFractionDigits: dec }) + "%";
  };

  const porcentajeSobreFacturacion = (valor, baseOverride = null) => {
    const base = baseOverride === null ? ytdFacturacion : baseOverride;
    return base > 0 ? formatPercentage((valor / base) * 100) : "0%";
  };

  // Puentes al motor único de costos: el EBITDAResultadoTable recalcula internamente,
  // pero todo lo que consume sale de `costos` — no hay un agregado paralelo.
  const totalCostosMes = useCallback(
    (monthIndex) => (monthIndex >= monthsToShow ? 0 : costosVariables[monthIndex] || 0),
    [costosVariables, monthsToShow]
  );
  // Lee de costosFijosPorConcepto, que ya aplica el switch por año. Antes iba directo
  // al motor de facturas y por eso el EBITDA de 2025 no cuadraba con el v1.
  const getCostoFijoValor = useCallback(
    (campo, i) => {
      const idx = camposCostosFijos.indexOf(campo);
      return (idx >= 0 ? costosFijosPorConcepto[idx]?.[i] : 0) || 0;
    },
    [costosFijosPorConcepto]
  );
  const getValueForMonthCosto = useCallback(
    (concepto, mes) => {
      const porConcepto = {
        "Servicios internet": aServiciosInternet,
        "Software DS": aSoftwareDS,
        Mantenimiento: aMantenimiento,
      };
      return { value: (porConcepto[concepto] || ZEROS)[mes - 1] || 0 };
    },
    [aServiciosInternet, aSoftwareDS, aMantenimiento]
  );

  const costosFijosYTD = ytd(costosFijos);

  // ─── AGREGADOS COMBINADOS (USD) PARA EL MODO REGIÓN ────────
  // El EBITDA de región = facturación − costos variables − costos fijos, todo en USD.
  // Sin estos arrays el hook recibia ceros y el EBITDA Run Rate salia IGUAL al de
  // Facturación (era el sintoma reportado). Cada pais se convierte con su propio TC.
  const aUSD = useCallback(
    (serieChile, seriePeru) =>
      ZEROS.map((_, i) => {
        const tcCL = dolarMensualChile[i] || dolarFallback?.Chile || 1;
        const tcPE = dolarMensualPeru[i] || dolarFallback?.["Perú"] || 1;
        return (serieChile[i] || 0) / tcCL + (seriePeru[i] || 0) / tcPE;
      }),
    [dolarMensualChile, dolarMensualPeru, dolarFallback]
  );

  // Costos MANUALES por pais (pre-2026). Mismos helpers que la vista de pais unico.
  const costosDePais = useCallback(
    (nombrePais, agg, hw, otrosBTLPais, negocios, dolarPais, fbPais) => {
      const m = manual.porPais?.[nombrePais];
      if (!m) return { variables: ZEROS, fijos: ZEROS, variablesFacturas: ZEROS, fijosFacturas: ZEROS };
      // Costo de carteleria del pais (v1: ER/index.js:2532). Es el termino que el v1
      // le RESTA al software del bloque A y le SUMA al del bloque C.
      const costoCart = (i) => {
        let pantallas = 0;
        (negocios || []).forEach((n) => {
          if (n.eliminada || n.estado !== "Activo" || !n.fecha_inicio) return;
          const fi = new Date(n.fecha_inicio);
          const plazo = parseInt(n.plazo_contrato_meses) || 0;
          if (plazo === 0) return;
          const ff = new Date(fi);
          ff.setMonth(fi.getMonth() + plazo);
          const medio = new Date(selectedYear, i, 15);
          if (medio >= fi && medio < ff) pantallas += parseInt(n.cantidad_pantallas) || 1;
        });
        return Math.round(pantallas * 10.99 * (dolarPais?.[i] || fbPais || 0));
      };
      // Proyeccion de "otros" de gestion (v1: ER/index.js:2510): promedio de los
      // valores > 0 del año, completando con el anterior. Se usa cuando NO hay fila.
      // El v1 proyecta sobre el MAPA de 12 entradas que devuelve getCostosGestion
      // (utils/gestion.js:20), no sobre las filas crudas: una entrada por mes, solo
      // filas globales (negocio_id null) y la ultima gana si hubiera repetidas.
      const aMapa = (filas) => {
        const mapa = Array.from({ length: 12 }, () => ({ software_ds: 0, otros: 0 }));
        (filas || []).forEach((row) => {
          // eslint-disable-next-line eqeqeq
          if (row.negocio_id != null) return;
          const i = Number(row.mes) - 1;
          if (i >= 0 && i < 12) {
            mapa[i].software_ds = Number(row.software_ds) || 0;
            mapa[i].otros = Number(row.otros) || 0;
          }
        });
        return mapa;
      };
      const proyectarGestion = (campo) => {
        const vals = [];
        const juntar = (mapa) =>
          mapa.forEach((c) => {
            if (vals.length >= 12) return;
            const v = Number(c[campo]) || 0;
            if (v > 0) vals.push(v);
          });
        juntar(aMapa(m.gestion));
        if (vals.length < 12) juntar(aMapa(m.gestionAA));
        return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
      };
      const proyOtros = proyectarGestion("otros");

      // Split A/C del v1 (ER/index.js:2575). Detalle que importa: el v1 resta la
      // carteleria SOLO cuando el valor del mes es real; cuando no hay dato y proyecta,
      // usa la proyeccion TAL CUAL, sin restar nada.
      const softwareA = ZEROS.map((_, i) => {
        const row = m.publicidad?.[i];
        if (row && row.software_ds != null) {
          return Math.max(0, (Number(row.software_ds) || 0) - costoCart(i));
        }
        return proyectarPublicidad(m.publicidad, m.publicidadAA, "software_ds", i);
      });

      // OJO — comportamiento real del v1 en el agregado de region: su `cgData` viene de
      // getCostosGestion, que devuelve un mapa de 12 entradas {software_ds, otros} SIN
      // campo `mes` (utils/gestion.js:20). Por eso su
      // `cgData.find(c => Number(c.mes) === i+1 ...)` NUNCA matchea, y el combinado
      // termina usando SIEMPRE el estimado de carteleria y SIEMPRE la proyeccion de
      // "otros", ignorando los valores por mes. Se replica tal cual: este es el unico
      // camino donde el v1 se comporta asi (la vista de pais unico si lee el valor real).
      const softwareC = ZEROS.map((_, i) => costoCart(i));
      const variables = sumar(
        serieManualPublicidad(m.publicidad, m.publicidadAA, "servicios_internet"),
        softwareA,
        serieManualPublicidad(m.publicidad, m.publicidadAA, "mantenimiento"),
        ...hw,
        softwareC,
        ZEROS.map(() => proyOtros),
        otrosBTLPais
      );
      const fijos = sumar(...camposCostosFijos.map((c) => serieManualFija(m.rawFijos, m.fijos, m.fijosAA, c)));

      // Rama FACTURAS (>= 2026) del MISMO país, con la misma composición que la vista de país
      // único: A = servicios_internet + max(0, software_ds − cartelería) + mantenimiento,
      // B = hardware, C = estimado de cartelería. En 2026 el manual de C ya devuelve 0
      // (getCostoGestionValue corta por año), así que el estimado es el único término, igual
      // que en país único. Instalación / Materiales / Otros de B, "otros" de gestión y otros
      // de BTL no tienen cuenta que los alimente: son 0 desde 2026 por diseño.
      const softwareAFacturas = ZEROS.map((_, i) => Math.max(0, (agg.porCuenta("software_ds")[i] || 0) - costoCart(i)));
      const variablesFacturas = sumar(
        agg.porCuenta("servicios_internet"),
        softwareAFacturas,
        agg.porCuenta("mantenimiento"),
        agg.porCuenta("hardware"),
        ZEROS.map((_, i) => costoCart(i)),
        // Contenidos digitales entra ENTERO, sin repartir: el reparto A/C es proporcional
        // puro (A + C === costo), así que el total del país es el mismo se mire por bloque
        // o agregado. El split solo existe para mostrarlo en dos filas.
        agg.porCuenta("contenidos_digitales")
      );
      const fijosFacturas = sumar(...camposCostosFijos.map((c) => agg.porCuenta(c)));

      return { variables, fijos, variablesFacturas, fijosFacturas };
    },
    [manual, selectedYear, serieManualPublicidad, serieManualFija]
  );

  const manualChile = costosDePais(
    "Chile",
    costosChile,
    [compraHardwareMensualChile, instalacionMensualChile, materialesMensualChile, otrosCostosMensualChile],
    otrosCostosBTLMensualChile,
    negociosCarteleriaChile,
    dolarMensualChile,
    dolarFallback?.Chile
  );
  const manualPeru = costosDePais(
    "Perú",
    costosPeru,
    [compraHardwareMensualPeru, instalacionMensualPeru, materialesMensualPeru, otrosCostosMensualPeru],
    otrosCostosBTLMensualPeru,
    negociosCarteleriaPeru,
    dolarMensualPeru,
    dolarFallback?.["Perú"]
  );

  const costosVariablesCombinadosUSD = useMemo(() => {
    if (!modoCombinado) return null;
    const varChile = sumar(
      pubAgenciaChile,
      comisionAgenciaContratosMensualChile,
      pubBrokerChile,
      comisionBrokerContratosMensualChile,
      pubTakeChile,
      pubApxChile,
      comisionApxContratosMensualChile,
      pubEdificiosChile,
      comisionEdificiosContratosMensualChile,
      comisionesFijasMensualChile,
      pubComercialChile,
      comisionComercialContratosMensualChile,
      comisionEdificiosBTLMensualChile,
      // Costo propio de Chile: desde 2026 las facturas DE CHILE; antes, sus tablas manuales.
      // Las dos ramas salen de `costosDePais("Chile", …)`, así que no dependen de la sesión.
      porAno(selectedYear, manualChile.variablesFacturas, manualChile.variables)
    );
    const varPeru = sumar(
      pubAgenciaPeru,
      comisionAgenciaContratosMensualPeru,
      pubBrokerPeru,
      comisionBrokerContratosMensualPeru,
      pubTakePeru,
      pubApxPeru,
      comisionApxContratosMensualPeru,
      pubEdificiosPeru,
      comisionEdificiosContratosMensualPeru,
      comisionesFijasMensualPeru,
      pubComercialPeru,
      comisionComercialContratosMensualPeru,
      comisionEdificiosBTLMensualPeru,
      // Peru no tiene SII/SUNAT: su agregado de facturas da 0 hoy. Se pasa igual (en vez de
      // ZEROS) para que el día que cargue documentos entre sin tocar esto.
      porAno(selectedYear, manualPeru.variablesFacturas, manualPeru.variables)
    );
    return aUSD(varChile, varPeru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Las series de la sesión (costosManualesA / bCompraHardware / softwareCarteleria) ya no
    // son deps: las dos patas salen de `costosDePais`, que trae su rama de facturas propia.
  }, [modoCombinado, aUSD, manualChile, manualPeru, selectedYear]);

  const costosFijosCombinadosUSD = useMemo(() => {
    if (!modoCombinado) return null;
    // Fijos = RRHH de cada pais + los conceptos, con el mismo switch por año. Los conceptos
    // salen del agregado DE ESE PAÍS (costosDePais), no de la serie de la sesión.
    const fijosChile = sumar(rrhhMensualChile, porAno(selectedYear, manualChile.fijosFacturas, manualChile.fijos));
    const fijosPeru = sumar(rrhhMensualPeru || ZEROS, porAno(selectedYear, manualPeru.fijosFacturas, manualPeru.fijos));
    return aUSD(fijosChile, fijosPeru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoCombinado, aUSD, rrhhMensualChile, rrhhMensualPeru, manualChile, manualPeru, selectedYear]);

  // ─── KPIs DEL HEADER ────────────────────────────────────────
  // Los run rates salen de useEBITDACalcs (hook puro del v1, importado en lectura):
  // misma fórmula, incluida la rama de modo combinado. No se reimplementa nada.
  const facturacionAAmensual = useMemo(
    () => sumar(pubIngresosAA, ingresosContratosMensualAnoAnterior),
    [pubIngresosAA, ingresosContratosMensualAnoAnterior]
  );

  const {
    ebitdaMensual,
    runRateAnual,
    facturacionUltimos12Meses,
    hardwareMensualesCombinados,
    btlMensualesCombinados,
    depreciacionMensualesCombinados,
    amortizacionMensualesCombinados,
    runRateParaMostrar,
    ebitdaRunRateParaMostrar,
    variacionFactParaMostrar,
    variacionEbitdaParaMostrar,
  } = useEBITDACalcs({
    valoresMensuales: facturacionA,
    valoresMensualesAnoAnterior: facturacionAAmensual,
    ingresosHardwareMensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosHardwareMensualChile,
    ingresosHardwareMensualPeru,
    ingresosGestionContenidosMensual,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensual,
    ingresosBTLMensualAnoAnterior,
    ingresosBTLMensualChile,
    ingresosBTLMensualPeru,
    valoresMensualesComisionAgencia: costoIntermediacion,
    valoresMensualesComisionAgenciaAnoAnterior: sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior),
    valoresMensualesComisionBroker: ZEROS,
    valoresMensualesComisionBrokerAnoAnterior: sumar(pubBrokerAA, comisionBrokerContratosMensualAnoAnterior),
    valoresMensualesComisiones: comisionEdificios,
    valoresMensualesComisionesAnoAnterior: sumar(pubEdificiosAA, comisionEdificiosContratosMensualAnoAnterior),
    valoresMensualesComisionComercial: { valores: comisionComercial },
    valoresMensualesComisionComercialAnoAnterior: sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior),
    valoresMensualesTake: ZEROS,
    valoresMensualesTakeAnoAnterior: pubTakeAA,
    valoresMensualesApx: ZEROS,
    valoresMensualesApxAnoAnterior: sumar(pubApxAA, comisionApxContratosMensualAnoAnterior),
    compraHardwareMensual: bCompraHardware,
    instalacionMensual: bInstalacion,
    materialesMensual: bMateriales,
    otrosCostosMensual: bOtrosHardware,
    compraHardwareMensualAnoAnterior: ZEROS,
    instalacionMensualAnoAnterior: ZEROS,
    materialesMensualAnoAnterior: ZEROS,
    otrosCostosMensualAnoAnterior: ZEROS,
    comisionEdificiosBTLMensual,
    otrosCostosBTLMensual: otrosBTL,
    comisionEdificiosBTLMensualAnoAnterior,
    otrosCostosBTLMensualAnoAnterior: ZEROS,
    getCostoGestionValue,
    getCostoGestionValueAnoAnterior: () => 0,
    calcularCostoSoftwareEstimado,
    getValueForMonth: getValueForMonthCosto,
    conceptosManuales: CONCEPTOS_MANUALES_A,
    costosPublicidadDataAnoAnterior: [],
    sumarCostosFijosMes: (i) => costosFijos[i] || 0,
    costosFijosMensualAnoAnterior: ZEROS,
    costosFijosMensualesCombinados: costosFijosCombinadosUSD || ZEROS,
    publicidadMensualesCombinados: sumar(pubCombinados, contratosMensualesCombinados),
    gestionMensualesCombinados,
    costosVariablesMensualesCombinados: costosVariablesCombinadosUSD || ZEROS,
    depreciacionMensualChile,
    depreciacionMensualPeru,
    amortizacionMensualChile,
    amortizacionMensualPeru,
    dolarMensual,
    dolarMensualAnoAnterior,
    dolarMensualChile,
    dolarMensualPeru,
    monthsToShow,
    selectedYear,
    currentYear,
    modoCombinado,
    pais,
    verEnDolares,
    dolarFallback,
  });

  // En modo combinado la tabla muestra los agregados de region (ya en USD), igual que
  // el v1 (ER/index.js:3484 y ss.). Sin esto el v2 mostraba solo el pais de sesion.
  const publicidadCombinadoTotal = useMemo(
    () => sumar(pubCombinados, contratosMensualesCombinados),
    [pubCombinados, contratosMensualesCombinados]
  );
  const facturacionCombinada = useMemo(
    () =>
      sumar(publicidadCombinadoTotal, hardwareMensualesCombinados, gestionMensualesCombinados, btlMensualesCombinados),
    [publicidadCombinadoTotal, hardwareMensualesCombinados, gestionMensualesCombinados, btlMensualesCombinados]
  );
  const facturacionMostrar = modoCombinado ? facturacionCombinada : facturacion;
  const publicidadMostrar = modoCombinado ? publicidadCombinadoTotal : facturacionA;
  const costosVariablesMostrar = modoCombinado ? costosVariablesCombinadosUSD || ZEROS : costosVariables;
  const beneficioBrutoMostrar = restar(facturacionMostrar, costosVariablesMostrar);
  const costosFijosMostrar = modoCombinado ? costosFijosCombinadosUSD || ZEROS : costosFijos;

  // ─── Acumulados (checkbox del header). Portado literal de ER/index.js:3248. ───
  const acumulados = useMemo(() => {
    const intUSD = interesesMensualesUSD[pais] || 0;
    const impUSD = impuestosMensualesUSD[pais] || 0;
    const rateArr = pais === "Chile" ? dolarMensual : dolarMensualPeru;
    const fb = pais === "Chile" ? dolarFallback.Chile : dolarFallback["Perú"];

    let ebitdaAcum = 0;
    let ebitAcum = 0;
    let resultadoAcum = 0;
    const ebitdaAcumArr = [];
    const ebitAcumArr = [];
    const resultadoAcumArr = [];

    for (let i = 0; i < 12; i++) {
      const eb = ebitdaMensual[i] || 0;
      const dep = depreciacionMensual[i] || 0;
      const amort = amortizacionMensual[i] || 0;
      const ebit = eb - dep - amort;
      const rate = rateArr[i] || fb;
      const resultado = ebit - intUSD * rate - impUSD * rate;

      if (i < monthsToShow) {
        ebitdaAcum += eb;
        ebitAcum += ebit;
        resultadoAcum += resultado;
      }
      ebitdaAcumArr.push(i < monthsToShow ? ebitdaAcum : null);
      ebitAcumArr.push(i < monthsToShow ? ebitAcum : null);
      resultadoAcumArr.push(i < monthsToShow ? resultadoAcum : null);
    }
    return { ebitdaAcum: ebitdaAcumArr, ebitAcum: ebitAcumArr, resultadoAcum: resultadoAcumArr };
  }, [
    ebitdaMensual,
    depreciacionMensual,
    amortizacionMensual,
    pais,
    dolarMensual,
    dolarMensualPeru,
    dolarFallback,
    monthsToShow,
  ]);

  // ─── Checkbox "Activos": series derivadas (ER/index.js:2689, :2701, :2841, :3229) ───
  const enUSDPorPais = useCallback(
    (serieChile, seriePeru) =>
      ZEROS.map((_, i) => {
        const tcCL = dolarMensualChile[i] || dolarFallback?.Chile || 1;
        const tcPE = dolarMensualPeru[i] || dolarFallback?.["Perú"] || 1;
        return (serieChile[i] || 0) / tcCL + (seriePeru[i] || 0) / tcPE;
      }),
    [dolarMensualChile, dolarMensualPeru, dolarFallback]
  );

  const inversionMensualesCombinados = useMemo(
    () => (modoCombinado ? enUSDPorPais(inversionMensualChile, inversionMensualPeru) : null),
    [modoCombinado, enUSDPorPais, inversionMensualChile, inversionMensualPeru]
  );
  const valorActivosCombinados = useMemo(
    () => (modoCombinado ? enUSDPorPais(valorActivosMensualChile, valorActivosMensualPeru) : null),
    [modoCombinado, enUSDPorPais, valorActivosMensualChile, valorActivosMensualPeru]
  );

  // El benchmark es el 10% de la facturacion; el disponible, su acumulado menos la
  // inversion acumulada. Antes de 2026 el v1 no los muestra (null).
  const eBenchmarkInv = useMemo(
    () => (selectedYear < 2026 ? null : facturacion.map((v) => (v || 0) * 0.1)),
    [selectedYear, facturacion]
  );
  const eDisponibleInv = useMemo(() => {
    if (selectedYear < 2026) return null;
    let acumBench = 0;
    let acumInv = 0;
    return ZEROS.map((_, i) => {
      acumBench += (facturacion[i] || 0) * 0.1;
      acumInv += inversionMensual[i] || 0;
      return acumBench - acumInv;
    });
  }, [selectedYear, facturacion, inversionMensual]);

  const benchmarkInversionCombinados = useMemo(() => {
    if (!modoCombinado || selectedYear < 2026) return null;
    return ZEROS.map((_, i) => (facturacionCombinada[i] || 0) * 0.1);
  }, [modoCombinado, selectedYear, facturacionCombinada]);

  const disponibleInversionCombinados = useMemo(() => {
    if (!modoCombinado || selectedYear < 2026) return null;
    let acumBench = 0;
    let acumInv = 0;
    return ZEROS.map((_, i) => {
      acumBench += (facturacionCombinada[i] || 0) * 0.1;
      acumInv += inversionMensualesCombinados?.[i] || 0;
      return acumBench - acumInv;
    });
  }, [modoCombinado, selectedYear, facturacionCombinada, inversionMensualesCombinados]);

  // ─── Ventana movil de 12 meses (checkbox LTM) — ER/index.js:2870 y :2880 ───
  // Cada serie se combina con la del anio anterior; los primeros (12 - monthsToShow)
  // meses son del anio previo.
  const enVentana = useCallback(
    (actual, anterior) => (verUltimos12Meses ? windowArray(actual || ZEROS, anterior || ZEROS, monthsToShow) : actual),
    [verUltimos12Meses, monthsToShow]
  );

  const mesesWindowed = useMemo(() => {
    if (!verUltimos12Meses) return meses;
    return Array.from({ length: 12 }, (_, j) => {
      const mesReal = (monthsToShow + j) % 12;
      const anio = j < 12 - monthsToShow ? selectedYear - 1 : selectedYear;
      return `${meses[mesReal]} ${String(anio).slice(2)}`;
    });
  }, [verUltimos12Meses, monthsToShow, selectedYear]);

  // Series del anio anterior que necesita la ventana LTM.
  const facturacionAATotal = sumar(
    facturacionAAmensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensualAnoAnterior
  );
  const costosManualesAAA = sumar(
    costosAA.porCuenta("servicios_internet"),
    costosAA.porCuenta("software_ds"),
    costosAA.porCuenta("mantenimiento")
  );
  const costosVariablesAA = sumar(
    sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior),
    sumar(pubEdificiosAA, comisionEdificiosContratosMensualAnoAnterior),
    sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior),
    comisionEdificiosBTLMensualAnoAnterior,
    costosManualesAAA,
    costosAA.porCuenta("hardware")
  );
  const costosFijosAA = sumar(
    rrhhMensualConBeneficiosAnoAnterior,
    ...camposCostosFijos.map((campo) => costosAA.porCuenta(campo))
  );

  // Series finales: en LTM, ventana movil; si no, la serie del anio.
  const vFacturacion = enVentana(facturacionMostrar, facturacionAATotal);
  const vPublicidad = enVentana(publicidadMostrar, sumar(pubIngresosAA, ingresosContratosMensualAnoAnterior));
  const vHardware = enVentana(ingresosHardwareMensual, ingresosHardwareMensualAnoAnterior);
  const vGestion = enVentana(ingresosGestionContenidosMensual, ingresosGestionContenidosMensualAnoAnterior);
  const vBTL = enVentana(ingresosBTLMensual, ingresosBTLMensualAnoAnterior);
  const vIntermediacion = enVentana(
    costoIntermediacion,
    sumar(pubAgenciaAA, comisionAgenciaContratosMensualAnoAnterior)
  );
  const vComEdificios = enVentana(
    comisionEdificios,
    sumar(pubEdificiosAA, comisionEdificiosContratosMensualAnoAnterior)
  );
  const vComComercial = enVentana(
    comisionComercial,
    sumar(pubComercialAA, comisionComercialContratosMensualAnoAnterior)
  );
  const vServiciosInternet = enVentana(aServiciosInternet, costosAA.porCuenta("servicios_internet"));
  const vSoftwareDS = enVentana(aSoftwareDS, costosAA.porCuenta("software_ds"));
  const vMantenimiento = enVentana(aMantenimiento, costosAA.porCuenta("mantenimiento"));
  // Ventana LTM del reparto: el año anterior se reparte con las MISMAS pantallas de hoy (el
  // conteo es un snapshot, no tiene serie histórica). Pre-2026 el agregado da 0 igual.
  const repartoAA = (costosAA.porCuenta("contenidos_digitales") || ZEROS).map((monto) =>
    repartirPorPantallas(monto, pantallasPublicidad, pantallasCarteleria)
  );
  const vContenidosA = enVentana(
    aContenidosDigitales,
    repartoAA.map((r) => r.publicidad)
  );
  const vContenidosC = enVentana(
    cContenidosDigitales,
    repartoAA.map((r) => r.carteleria)
  );
  const vCompraHardware = enVentana(bCompraHardware, costosAA.porCuenta("hardware"));
  const vInstalacion = enVentana(bInstalacion, ZEROS);
  const vMateriales = enVentana(bMateriales, ZEROS);
  const vOtrosHardware = enVentana(bOtrosHardware, ZEROS);
  const vComEdificiosBTL = enVentana(comisionEdificiosBTLMensual, comisionEdificiosBTLMensualAnoAnterior);
  const vOtrosBTL = enVentana(otrosBTL, ZEROS);
  const vRRHH = enVentana(rrhhMensualConBeneficios, rrhhMensualConBeneficiosAnoAnterior);
  const vCostosVariables = enVentana(costosVariablesMostrar, costosVariablesAA);
  const vCostosFijos = enVentana(costosFijosMostrar, costosFijosAA);
  const vDepreciacion = enVentana(depreciacionMensual, ZEROS);
  const vAmortizacion = enVentana(amortizacionMensual, ZEROS);
  const vInversion = enVentana(inversionMensual, ZEROS);
  const vValorActivos = enVentana(valorActivosMensual, ZEROS);
  const vBeneficioBruto = restar(vFacturacion, vCostosVariables);
  const vContribucionA = restar(
    vPublicidad,
    vIntermediacion,
    vComEdificios,
    vComComercial,
    sumar(vServiciosInternet, vSoftwareDS, vMantenimiento, vContenidosA)
  );
  const vContribucionB = restar(vHardware, vCompraHardware, vInstalacion, vMateriales, vOtrosHardware);
  const vContribucionD = restar(vBTL, vComEdificiosBTL, vOtrosBTL);
  const vEbitda = restar(vFacturacion, vCostosVariables, vCostosFijos);

  // ─── Detalle de facturas por celda ──────────────────────────
  // Solo se habilita donde el valor es suma DIRECTA de facturas: año >= 2026, vista de
  // pais unico y sin ventana LTM (que mezcla meses de dos años en un mismo slot).
  // Quedan fuera a proposito: las celdas pre-2026 (carga manual), el Software DS de
  // carteleria (es un estimado, y el split A/C impide garantizar total == celda), las DOS
  // filas de Contenidos Digitales (celdas REPARTIDAS: el modal mostraria las facturas
  // enteras y no cuadraria con la celda) y las lineas OP computadas (comisiones, RRHH,
  // depreciacion).
  const detalleDisponible = useCallback(
    (i) => selectedYear >= ANO_INICIO_FACTURAS && !modoCombinado && !verUltimos12Meses && i < monthsToShow,
    [selectedYear, modoCombinado, verUltimos12Meses, monthsToShow]
  );

  const onDetalleFactura = useMemo(
    () => ({
      disponible: detalleDisponible,
      abrir: (cuenta, titulo, mesIndex) => setDetalleFacturas({ cuenta, titulo, mesIndex }),
    }),
    [detalleDisponible]
  );

  const ytdFacturacion = ytd(vFacturacion);
  const ytdPublicidad = ytd(vPublicidad);
  const margenFinal = ytdFacturacion - ytd(vCostosVariables);

  // "vs YTD" y "vs AA": mismas fórmulas que el v1 (ER/index.js:3008 y :3042).
  const facturacionAnoAnteriorTotal = useMemo(
    () =>
      facturacionAAmensual.reduce((a, b) => a + b, 0) +
      ingresosHardwareMensualAnoAnterior.reduce((a, b) => a + b, 0) +
      ingresosGestionContenidosMensualAnoAnterior.reduce((a, b) => a + b, 0) +
      ingresosBTLMensualAnoAnterior.reduce((a, b) => a + b, 0),
    [
      facturacionAAmensual,
      ingresosHardwareMensualAnoAnterior,
      ingresosGestionContenidosMensualAnoAnterior,
      ingresosBTLMensualAnoAnterior,
    ]
  );

  const ytdAnoAnterior = useMemo(() => {
    if (monthsToShow === 12) return facturacionAnoAnteriorTotal;
    const corte = (arr) => arr.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
    return (
      corte(facturacionAAmensual) +
      corte(ingresosHardwareMensualAnoAnterior) +
      corte(ingresosGestionContenidosMensualAnoAnterior) +
      corte(ingresosBTLMensualAnoAnterior)
    );
  }, [
    monthsToShow,
    facturacionAnoAnteriorTotal,
    facturacionAAmensual,
    ingresosHardwareMensualAnoAnterior,
    ingresosGestionContenidosMensualAnoAnterior,
    ingresosBTLMensualAnoAnterior,
  ]);

  // Variantes de región (modo combinado) de las dos YoY. El v1 las deriva de
  // `historiaER` (su caché en localStorage) y de constantes historicas de 2024, que el
  // v2 no tiene. Se dejan en null a proposito: el ERHeader muestra "—" cuando lo son,
  // igual que el v1 cuando no puede calcularlas. Portarlas requiere primero decidir de
  // donde sale la facturacion historica de region, y va en su propia pasada.
  const varYTDvsYTDRegion = null;
  const varYTDvsFullRegion = null;

  const varYTDvsYTD = ytdAnoAnterior > 0 ? Number(((ytdFacturacion / ytdAnoAnterior - 1) * 100).toFixed(1)) : null;
  const varYTDvsFull =
    facturacionAnoAnteriorTotal > 0
      ? Number(((ytdFacturacion / facturacionAnoAnteriorTotal - 1) * 100).toFixed(1))
      : null;

  // Base de las sub-filas del bloque A: el YTD de Publicidad, no el de Facturación
  // (v1: ER/index.js:3620, :3660, :3761, :3780).
  const pctSobrePublicidad = (valor) => (ytdPublicidad > 0 ? formatPercentage((valor / ytdPublicidad) * 100) : "0%");

  const margenPorcentaje = (margen, ingresos) => {
    if (!ingresos || ingresos === 0) return "0%";
    return formatPercentage((margen / ingresos) * 100);
  };

  // CostosFijosTable lee `costosFijosData` / `rawCostosFijos` (las tablas manuales del
  // v1). Se le sintetizan esas filas desde el motor único, con TODOS los campos
  // presentes: así `registroExiste` es siempre true y el componente nunca proyecta
  // (v2 no proyecta) ni pinta la celda de amarillo.
  // Filas sinteticas que consume CostosFijosTable. Se arman desde costosFijosPorConcepto,
  // que YA aplica el switch por año (facturas desde 2026, tablas manuales antes), asi que
  // el componente no vuelve a decidir la fuente. Todos los campos presentes => nunca
  // proyecta por su cuenta: la proyeccion pre-2026 ya viene resuelta en la serie.
  const costosFijosFilas = useMemo(
    () =>
      ZEROS.map((_, i) => {
        const fila = { mes: i + 1 };
        camposCostosFijos.forEach((campo, idx) => {
          fila[campo] = costosFijosPorConcepto[idx]?.[i] || 0;
        });
        return fila;
      }),
    [costosFijosPorConcepto]
  );

  const noop = useCallback(() => {}, []);

  // Fila genérica: 12 celdas + YTD. Los meses futuros muestran "-" como en el v1.
  // Fila genérica. La celda YTD usa `totalCell` (sin estilo propio → fuente normal),
  // igual que el v1; sólo FACTURACIÓN lleva `totalCellYTD`, que es la de 1.3rem. Pasar
  // por la clase equivocada era lo que agrandaba el alto de esas filas.
  const Row = ({
    label,
    values = ZEROS,
    className,
    conceptClassName,
    conceptStyle,
    onToggle,
    caret,
    porcentaje = null,
    totalGrande = false,
    yaEnUSD = false,
    cuentaDetalle = null,
  }) => (
    <tr
      className={className}
      onClick={onToggle}
      style={onToggle ? { cursor: "pointer", userSelect: "none" } : undefined}
    >
      <td className={conceptClassName || styles.conceptCell} style={conceptStyle}>
        {label}
        {caret ? ` ${caret}` : ""}
      </td>
      {ZEROS.map((_, i) => {
        const clickable = Boolean(cuentaDetalle) && detalleDisponible(i);
        return (
          <td
            key={i}
            className={styles.valueCell}
            style={clickable ? { cursor: "pointer" } : undefined}
            onClick={
              clickable
                ? (e) => {
                    e.stopPropagation();
                    onDetalleFactura.abrir(cuentaDetalle, label, i);
                  }
                : undefined
            }
          >
            {i < mesesVisibles ? formatCurrency(values[i] || 0, i, null, yaEnUSD) : "-"}
          </td>
        );
      })}
      <td className={totalGrande ? styles.totalCellYTD : styles.totalCell}>
        {formatCurrency(ytd(values), null, yaEnUSD ? null : values, yaEnUSD)}
        {porcentaje !== null ? ` | ${porcentaje}` : ""}
      </td>
    </tr>
  );

  if (!ADMIN_IDS.includes(Number(userId))) return null;

  return (
    <div className={styles.ERWrapper}>
      <ERHeader
        logo={pais === "Chile" ? wecastLogo : visionaLogo}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        verEnDolares={verEnDolares}
        setVerEnDolares={setVerEnDolares}
        verChileYPeru={verChileYPeru}
        setVerChileYPeru={setVerChileYPeru}
        modoCombinado={modoCombinado}
        pais={pais}
        moneda={moneda}
        isOportunidadesLoading={isLoading}
        varYTDvsYTD={varYTDvsYTD}
        varYTDvsFull={varYTDvsFull}
        runRateParaMostrar={runRateParaMostrar}
        runRateAnual={runRateAnual}
        facturacionUltimos12Meses={facturacionUltimos12Meses}
        variacionFactParaMostrar={variacionFactParaMostrar}
        ebitdaRunRateParaMostrar={ebitdaRunRateParaMostrar}
        variacionEbitdaParaMostrar={variacionEbitdaParaMostrar}
        varYTDvsYTDRegion={varYTDvsYTDRegion}
        varYTDvsFullRegion={varYTDvsFullRegion}
        es2026Habilitado={true}
        verUltimos12Meses={verUltimos12Meses}
        setVerUltimos12Meses={setVerUltimos12Meses}
        monthsToShow={monthsToShow}
        onOpenChart={() => {}}
        verAcumulados={verAcumulados}
        setVerAcumulados={setVerAcumulados}
        verUnitEconomics={verUnitEconomics}
        setVerUnitEconomics={setVerUnitEconomics}
        unitEconomicsReady={false}
        verInversionActivos={verInversionActivos}
        setVerInversionActivos={setVerInversionActivos}
      />
      <div className={styles.ER}>
        <table className={styles.tableER}>
          <thead>
            <tr>
              <th className={styles.conceptCell}>Concepto</th>
              {mesesWindowed.map((m, i) => (
                <th key={i} className={styles.valueCell}>
                  {m}
                </th>
              ))}
              <th className={styles.totalCellHead}>{verUltimos12Meses ? "LTM" : "YTD"}</th>
            </tr>
          </thead>
          <tbody>
            <Row
              label="FACTURACIÓN"
              values={vFacturacion}
              className={styles.facturacionRow}
              totalGrande={true}
              yaEnUSD={modoCombinado}
            />

            {/* === BLOQUE A — Publicidad === */}
            <Row
              label="A - Publicidad"
              values={vPublicidad}
              yaEnUSD={modoCombinado}
              porcentaje={porcentajeSobreFacturacion(ytdPublicidad)}
              className={styles.sectionHeader}
              caret={bloqueACollapsed ? ">" : ""}
              onToggle={() => setBloqueACollapsed(!bloqueACollapsed)}
            />
            {!bloqueACollapsed && (
              <>
                <Row
                  label="Costo intermediación"
                  values={vIntermediacion}
                  porcentaje={pctSobrePublicidad(ytd(costoIntermediacion))}
                />
                <Row
                  label="Comisión edificios"
                  values={vComEdificios}
                  porcentaje={pctSobrePublicidad(ytd(comisionEdificios))}
                />
                <Row
                  label="Comisión comercial"
                  values={vComComercial}
                  porcentaje={pctSobrePublicidad(ytd(comisionComercial))}
                />
                <Row
                  label="Servicios internet"
                  values={vServiciosInternet}
                  cuentaDetalle="servicios_internet"
                  porcentaje={pctSobrePublicidad(ytd(aServiciosInternet))}
                />
                <Row label="Software DS" values={vSoftwareDS} porcentaje={pctSobrePublicidad(ytd(aSoftwareDS))} />
                <Row
                  label="Mantenimiento"
                  values={vMantenimiento}
                  cuentaDetalle="mantenimiento"
                  porcentaje={pctSobrePublicidad(ytd(aMantenimiento))}
                />
                {/* NO clickeable, igual que Software DS: es una celda REPARTIDA y el modal
                    no puede cuadrar con ella sin falsear los importes de las facturas. */}
                <Row
                  label="Contenidos Digitales"
                  values={vContenidosA}
                  porcentaje={pctSobrePublicidad(ytd(aContenidosDigitales))}
                />
                <Row
                  label="Contribución"
                  values={vContribucionA}
                  porcentaje={margenPorcentaje(ytd(contribucionA), ytdPublicidad)}
                  className={styles.marginRow}
                  conceptClassName={styles.conceptCellMargen}
                />
              </>
            )}

            {/* === BLOQUE B — Hardware === */}
            <BloqueBTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              bloqueBCollapsed={bloqueBCollapsed}
              setBloqueBCollapsed={setBloqueBCollapsed}
              hardwareMensualesCombinados={hardwareMensualesCombinados || ZEROS}
              ingresosHardwareMensual={vHardware}
              monthsToShow={mesesVisibles}
              openHardwareIngresosModal={noop}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              ytdIngresosHardware={ytd(vHardware)}
              compraHardwareMensual={vCompraHardware}
              instalacionMensual={vInstalacion}
              materialesMensual={vMateriales}
              otrosCostosMensual={vOtrosHardware}
              openHardwareFlexModal={noop}
              onDetalleFactura={onDetalleFactura}
              ytdCompraHardware={ytd(vCompraHardware)}
              ytdInstalacion={ytd(vInstalacion)}
              ytdMateriales={ytd(vMateriales)}
              ytdOtrosCostosHardware={ytd(vOtrosHardware)}
              ytdMargenHardware={ytd(vContribucionB)}
              margenPorcentaje={margenPorcentaje}
            />

            {/* === BLOQUE C — Gestión & Contenidos === */}
            <BloqueCTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              bloqueCCollapsed={bloqueCCollapsed}
              setBloqueCCollapsed={setBloqueCCollapsed}
              gestionMensualesCombinados={gestionMensualesCombinados || ZEROS}
              ingresosGestionContenidosMensual={vGestion}
              detalleGestionContenidosPorMes={detalleGestionContenidosPorMes}
              showGestionContenidosModal={noop}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              ytdIngresosGestionContenidos={ytdIngresosGestionContenidos}
              getCostoGestionValue={getCostoGestionValue}
              calcularCostoSoftwareEstimado={calcularCostoSoftwareEstimado}
              verEnDolares={verEnDolares}
              selectedYear={selectedYear}
              getDolarValue={getDolarValue}
              localString={localString}
              pais={pais}
              puedeEditar={false}
              openSoftwareDSModalC={noop}
              openEditGestion={noop}
              ytdSoftwareDS={ytdSoftwareDS}
              ytdOtros={ytdOtros}
              margenGestionContenidosMensual={margenGestionContenidosAjustado}
              ytdMargenGestionContenidos={ytdMargenGestionContenidosAjustado}
              contenidosDigitales={vContenidosC}
              ytdContenidosDigitales={ytd(vContenidosC)}
              margenPorcentaje={margenPorcentaje}
              monthsToShow={mesesVisibles}
            />

            {/* === BLOQUE D — BTL & otros === */}
            <BloqueDTable
              modoCombinado={modoCombinado}
              verUltimos12Meses={verUltimos12Meses}
              bloqueDCollapsed={bloqueDCollapsed}
              setBloqueDCollapsed={setBloqueDCollapsed}
              btlMensualesCombinados={btlMensualesCombinados || ZEROS}
              ingresosBTLMensual={vBTL}
              monthsToShow={mesesVisibles}
              openBTLModal={noop}
              formatCurrency={formatCurrency}
              formatPercentage={formatPercentage}
              ytdIngresosBTL={ytd(vBTL)}
              comisionEdificiosBTLMensual={vComEdificiosBTL}
              ytdComisionEdificiosBTL={ytd(vComEdificiosBTL)}
              otrosCostosBTLMensual={vOtrosBTL}
              ytdOtrosCostosBTL={ytd(vOtrosBTL)}
              margenBTLMensual={vContribucionD}
              ytdMargenBTL={ytd(vContribucionD)}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              margenPorcentaje={margenPorcentaje}
            />

            {/* Mismas clases que el v1 (ER/index.js:3889 y :3910): bordó y azul, no verde. */}
            <Row
              label="Costos Variables"
              values={vCostosVariables}
              yaEnUSD={modoCombinado}
              porcentaje={porcentajeSobreFacturacion(ytd(costosVariablesMostrar))}
              className={styles.totalCostosRow}
            />
            <Row
              label="Beneficio Bruto"
              values={vBeneficioBruto}
              yaEnUSD={modoCombinado}
              porcentaje={porcentajeSobreFacturacion(ytd(beneficioBrutoMostrar))}
              className={styles.margenFinalRow}
              conceptClassName={styles.conceptCellMargen}
            />

            {/* === COSTOS FIJOS === */}
            <CostosFijosTable
              modoCombinado={modoCombinado}
              costosFijosCollapsed={costosFijosCollapsed}
              setCostosFijosCollapsed={setCostosFijosCollapsed}
              costosFijosMensualesCombinados={costosFijosCombinadosUSD || ZEROS}
              monthsToShow={mesesVisibles}
              sumarCostosFijosMes={(i) => vCostosFijos[i] || 0}
              formatCurrency={formatCurrency}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              costosFijosYTD={costosFijosYTD}
              rrhhCollapsed={rrhhCollapsed}
              setRrhhCollapsed={setRrhhCollapsed}
              isEmpleadosRRHHLoading={isEmpleadosRRHHLoading}
              rrhhMensualConBeneficios={vRRHH}
              empleadosRRHH={empleadosRRHH}
              getSueldoBaseVigente={getSueldoBaseVigente}
              selectedYear={selectedYear}
              pais={pais}
              puedeEditar={false}
              nombresCostosFijos={nombresCostosFijos}
              camposCostosFijos={camposCostosFijos}
              meses={meses}
              rawCostosFijos={costosFijosFilas}
              costosFijosData={costosFijosFilas}
              getProjectedCostoFijo={() => 0}
              verEnDolares={verEnDolares}
              getDolarValue={getDolarValue}
              dolarMensual={dolarMensual}
              localString={localString}
              openEditModal={noop}
              onDetalleFactura={onDetalleFactura}
              verUltimos12Meses={verUltimos12Meses}
              costosFijosMensualWindowed={vCostosFijos}
            />

            {/* === EBITDA → RESULTADO === componente clonado del v1: trae los colapsos de
                EBIT (Depreciación/Amortización) y Beneficio Neto (Intereses/Impuestos),
                sus flechas, los colores y la columna YTD con %. */}
            <EBITDAResultadoTable
              modoCombinado={modoCombinado}
              monthsToShow={mesesVisibles}
              formatCurrency={formatCurrency}
              porcentajeSobreFacturacion={porcentajeSobreFacturacion}
              publicidadMensualesCombinados={publicidadCombinadoTotal}
              hardwareMensualesCombinados={hardwareMensualesCombinados || ZEROS}
              gestionMensualesCombinados={gestionMensualesCombinados || ZEROS}
              btlMensualesCombinados={btlMensualesCombinados || ZEROS}
              costosVariablesMensualesCombinados={costosVariablesCombinadosUSD || ZEROS}
              costosFijosMensualesCombinados={costosFijosCombinadosUSD || ZEROS}
              valoresMensuales={facturacionA}
              ingresosHardwareMensual={ingresosHardwareMensual}
              ingresosGestionContenidosMensual={ingresosGestionContenidosMensual}
              ingresosBTLMensual={ingresosBTLMensual}
              camposCostosFijos={camposCostosFijos}
              getCostoFijoValor={getCostoFijoValor}
              rrhhMensualConBeneficios={rrhhMensualConBeneficios}
              totalCostosMes={totalCostosMes}
              margenFinal={margenFinal}
              costosFijosYTD={costosFijosYTD}
              sumarCostosFijosMes={(i) => costosFijos[i] || 0}
              valoresMensualesComisionAgencia={costoIntermediacion}
              valoresMensualesComisiones={comisionEdificios}
              valoresMensualesComisionComercial={{ valores: comisionComercial }}
              conceptosManuales={CONCEPTOS_MANUALES_A}
              getValueForMonth={getValueForMonthCosto}
              compraHardwareMensual={bCompraHardware}
              instalacionMensual={vInstalacion}
              materialesMensual={vMateriales}
              otrosCostosMensual={vOtrosHardware}
              getCostoGestionValue={getCostoGestionValue}
              comisionEdificiosBTLMensual={comisionEdificiosBTLMensual}
              otrosCostosBTLMensual={vOtrosBTL}
              ebitCollapsed={ebitCollapsed}
              setEbitCollapsed={setEbitCollapsed}
              depreciacionMensualesCombinados={depreciacionMensualesCombinados || ZEROS}
              depreciacionMensual={vDepreciacion}
              amortizacionMensualesCombinados={amortizacionMensualesCombinados || ZEROS}
              amortizacionMensual={vAmortizacion}
              meses={mesesWindowed}
              resultadoCollapsed={resultadoCollapsed}
              setResultadoCollapsed={setResultadoCollapsed}
              interesesMensualesUSD={interesesMensualesUSD}
              impuestosMensualesUSD={impuestosMensualesUSD}
              verChileYPeru={modoCombinado}
              verEnDolares={verEnDolares}
              pais={pais}
              moneda={moneda}
              dolarMensual={dolarMensual}
              dolarMensualPeru={dolarMensualPeru}
              dolarFallback={dolarFallback}
              setIsInteresesModalOpen={() => {}}
              verUltimos12Meses={verUltimos12Meses}
              ebitdaMensualWindowed={vEbitda}
              facturacionMensualWindowed={vFacturacion}
              costosVariablesMensualWindowed={vCostosVariables}
              costosFijosMensualWindowed={vCostosFijos}
            />
            <InversionActivosTable
              verChileYPeru={modoCombinado}
              verUnitEconomics={verUnitEconomics}
              verInversionActivos={verInversionActivos}
              selectedYear={selectedYear}
              inversionCollapsed={inversionCollapsed}
              setInversionCollapsed={setInversionCollapsed}
              mesesWindowed={mesesWindowed}
              eMs={mesesVisibles}
              formatCurrency={formatCurrency}
              eBenchmarkInv={eBenchmarkInv}
              eInversion={vInversion}
              eDisponibleInv={eDisponibleInv}
              eValorActivos={vValorActivos}
              inversionMensualesCombinados={inversionMensualesCombinados}
              disponibleInversionCombinados={disponibleInversionCombinados}
              valorActivosCombinados={valorActivosCombinados}
              benchmarkInversionCombinados={benchmarkInversionCombinados}
              verUltimos12Meses={verUltimos12Meses}
              meses={meses}
              monthsToShow={monthsToShow}
            />
            {verAcumulados && (
              <>
                <tr style={{ height: "4px", backgroundColor: "transparent" }}>
                  <td colSpan={14} style={{ padding: 0, border: "none" }} />
                </tr>
                {[
                  { label: "EBITDA", data: acumulados.ebitdaAcum, color: "#27ae60" },
                  { label: "EBIT", data: acumulados.ebitAcum, color: "#2980b9" },
                  { label: "Beneficio Neto", data: acumulados.resultadoAcum, color: "#bb6bd9" },
                ].map(({ label, data, color }) => (
                  <tr key={label} style={{ backgroundColor: "#1a1a2e" }}>
                    <td
                      className={styles.conceptCell}
                      style={{ textAlign: "left", color, fontWeight: "600", fontSize: "0.85rem" }}
                    >
                      {label}
                    </td>
                    {meses.map((_, index) => {
                      const val = data[index];
                      return (
                        <td
                          key={index}
                          className={styles.valueCell}
                          style={{
                            fontSize: "0.8rem",
                            color: val !== null && val < 0 ? "#e74c3c" : "#ccc",
                            fontWeight: "500",
                            padding: "6px 4px",
                            textAlign: "right",
                          }}
                        >
                          {val === null ? "-" : formatCurrency(val, index)}
                        </td>
                      );
                    })}
                    <td
                      className={styles.valueCell}
                      style={{
                        fontSize: "0.8rem",
                        color: data[Math.min(monthsToShow, 12) - 1] < 0 ? "#e74c3c" : color,
                        fontWeight: "600",
                        padding: "6px 4px",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(data[Math.min(monthsToShow, 12) - 1] || 0, Math.min(monthsToShow, 12) - 1)}
                    </td>
                  </tr>
                ))}
              </>
            )}
            <TipoCambioTable
              verEnDolares={verEnDolares}
              verChileYPeru={modoCombinado}
              mesesWindowed={mesesWindowed}
              eMs={mesesVisibles}
              eDolar={enVentana(dolarMensual, dolarMensualAnoAnterior)}
              moneda={moneda}
              localString={localString}
              pais={pais}
              meses={meses}
              monthsToShow={mesesVisibles}
              dolarMensual={dolarMensual}
              dolarMensualOtroPais={dolarMensualOtroPais}
            />
          </tbody>
        </table>
        <DetalleFacturasModal
          isOpen={Boolean(detalleFacturas)}
          titulo={detalleFacturas?.titulo || ""}
          mes={detalleFacturas ? mesesCompletos[detalleFacturas.mesIndex] : ""}
          facturas={detalleFacturas ? costos.detalleDe(detalleFacturas.cuenta, detalleFacturas.mesIndex) : []}
          formatCurrency={formatCurrency}
          onClose={() => setDetalleFacturas(null)}
        />
        {dtesSinVincular > 0 && (
          <Link
            to="/admin/facturacion"
            style={{
              display: "inline-block",
              textAlign: "left",
              padding: "4px 8px",
              margin: "24px 0 0",
              background: "#ffecd2",
              borderRadius: 4,
              color: "#e67e22",
              fontSize: "0.7rem",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {dtesSinVincular} factura{dtesSinVincular === 1 ? "" : "s"} del {enteFiscal} sin clasificar para{" "}
            {selectedYear} → no entran al ER hasta asignarles cuenta
          </Link>
        )}
        {ventasCerradasSinVincular && (
          <div style={{ marginTop: 6 }}>
            <span
              style={{
                display: "inline-block",
                textAlign: "left",
                padding: "4px 8px",
                background: "#e8f4fd",
                borderRadius: 4,
                color: "#2980b9",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              {ventasCerradasSinVincular.count} venta{ventasCerradasSinVincular.count === 1 ? "" : "s"} pendiente
              {ventasCerradasSinVincular.count === 1 ? "" : "s"} de vincular en{" "}
              {mesesCompletos[ventasCerradasSinVincular.mesIdx]} por {formatCurrency(ventasCerradasSinVincular.total)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
